import React, { useState, useEffect } from "react";

const MultiCarousel = ({
  children,
  customLeftArrow,
  customRightArrow,
  onSlideChange,
  responsive = {
    desktop: {
      breakpoint: { max: 3000, min: 1024 },
      items: 3,
      slidesToSlide: 3,
    },
    tablet: { breakpoint: { max: 1024, min: 464 }, items: 2, slidesToSlide: 2 },
    mobile: { breakpoint: { max: 464, min: 0 }, items: 1, slidesToSlide: 1 },
  },
  infinite = true,
  containerClass = "",
  itemClass = "",
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsToShow, setItemsToShow] = useState(3);
  const [slidesToSlide, setSlidesToSlide] = useState(3);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );

  const totalItems = React.Children.count(children);
  const childrenArray = React.Children.toArray(children);

  // Handle window resize
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Update items to show based on screen size
  useEffect(() => {
    let currentBreakpoint = responsive.desktop;

    if (windowWidth <= responsive.mobile.breakpoint.max) {
      currentBreakpoint = responsive.mobile;
    } else if (windowWidth <= responsive.tablet.breakpoint.max) {
      currentBreakpoint = responsive.tablet;
    }

    setItemsToShow(currentBreakpoint.items);
    setSlidesToSlide(
      currentBreakpoint.slidesToSlide || currentBreakpoint.items
    );
    setCurrentIndex(0); // Reset index when breakpoint changes
  }, [windowWidth, responsive]);

  const maxIndex = Math.max(0, totalItems - itemsToShow);

  const goToPrevious = () => {
    let newIndex;
    if (infinite) {
      if (currentIndex <= 0) {
        newIndex = maxIndex;
      } else {
        newIndex = Math.max(0, currentIndex - slidesToSlide);
      }
    } else {
      newIndex = Math.max(0, currentIndex - slidesToSlide);
    }
    setCurrentIndex(newIndex);
    if (onSlideChange) {
      onSlideChange(newIndex);
    }
  };

  const goToNext = () => {
    let newIndex;
    if (infinite) {
      if (currentIndex >= maxIndex) {
        newIndex = 0;
      } else {
        newIndex = Math.min(maxIndex, currentIndex + slidesToSlide);
      }
    } else {
      newIndex = Math.min(maxIndex, currentIndex + slidesToSlide);
    }
    setCurrentIndex(newIndex);
    if (onSlideChange) {
      onSlideChange(newIndex);
    }
  };

  const shouldShowArrows = totalItems > itemsToShow;
  const translateX = (currentIndex * 100) / totalItems;

  const carouselStyles = {
    container: {
      position: "relative",
      width: "100%",
      overflow: "hidden",
    },
    wrapper: {
      position: "relative",
      display: "flex",
      alignItems: "center",
    },
    content: {
      flex: 1,
      overflow: "hidden",
      position: "relative",
    },
    track: {
      display: "flex",
      transform: `translateX(-${translateX}%)`,
      transition: "transform 0.3s ease-in-out",
      willChange: "transform",
      padding: "0 10px",
    },
    slide: {
      flexShrink: 0,
      width: `${100 / itemsToShow}%`,
      padding: "0 5px",
      boxSizing: "border-box",
    },
    arrow: {
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      zIndex: 2,
      cursor: "pointer",
      userSelect: "none",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      opacity: 0.8,
      transition: "opacity 0.2s ease",
    },
    arrowLeft: {
      left: "10px",
    },
    arrowRight: {
      right: "10px",
    },
    arrowSpan: {
      fontSize: "2rem",
      fontWeight: "bold",
      color: "#333",
      background: "rgba(255, 255, 255, 0.8)",
      borderRadius: "50%",
      width: "40px",
      height: "40px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
    },
  };

  return (
    <div
      className={`multi-carousel-container ${containerClass}`}
      style={carouselStyles.container}
    >
      <div className="multi-carousel-wrapper" style={carouselStyles.wrapper}>
        {/* Left Arrow */}
        {shouldShowArrows && (infinite || currentIndex > 0) && (
          <div
            className="multi-carousel-arrow multi-carousel-arrow-left"
            onClick={goToPrevious}
            style={{ ...carouselStyles.arrow, ...carouselStyles.arrowLeft }}
          >
            {customLeftArrow ? (
              React.cloneElement(customLeftArrow, { onClick: goToPrevious })
            ) : (
              <span style={carouselStyles.arrowSpan}>‹</span>
            )}
          </div>
        )}

        {/* Carousel Content */}
        <div className="multi-carousel-content" style={carouselStyles.content}>
          <div className="multi-carousel-track" style={carouselStyles.track}>
            {childrenArray.map((child, index) => (
              <div
                key={index}
                className={`multi-carousel-slide ${itemClass}`}
                style={carouselStyles.slide}
              >
                {child}
              </div>
            ))}
          </div>
        </div>

        {/* Right Arrow */}
        {shouldShowArrows && (infinite || currentIndex < maxIndex) && (
          <div
            className="multi-carousel-arrow multi-carousel-arrow-right"
            onClick={goToNext}
            style={{ ...carouselStyles.arrow, ...carouselStyles.arrowRight }}
          >
            {customRightArrow ? (
              React.cloneElement(customRightArrow, { onClick: goToNext })
            ) : (
              <span style={carouselStyles.arrowSpan}>›</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MultiCarousel;
